import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ApiClientOptions } from '../api/client';
import { isApiError } from '../api/client';
import { ApiContext, useApi } from '../api/context';
import { queryKeys } from '../api/queries';
import { Button, ErrorMessage, Loading } from '../components/ui';
import { strings } from '../strings';
import { bootstrap, type BootResult } from './bootstrap';
import { AuthContext, type AuthState } from './context';
import { endRevealSession } from '../variables/reveal';
import { signOut } from './signOut';

type Boot = { status: 'loading' } | { status: 'error'; error: unknown } | ({ status: 'ready' } & BootResult);

/**
 * Loads the configuration, signs in when the API uses `jwt`, and loads the current user and
 * roles from `GET /me` (after sign-in and after each token renewal). Renders children when ready.
 */
export function AuthGate({ options, children }: { options: ApiClientOptions; children: ReactNode }) {
  const [boot, setBoot] = useState<Boot>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let canceled = false;
    bootstrap(options)
      .then((result) => {
        if (!canceled && result) setBoot({ status: 'ready', ...result });
      })
      .catch((error: unknown) => {
        if (!canceled) setBoot({ status: 'error', error });
      });
    return () => {
      canceled = true;
    };
  }, [options, attempt]);

  if (boot.status === 'loading') return <Loading label={strings.auth.signingIn} />;
  if (boot.status === 'error') {
    return (
      <div className="p-4">
        <h1 className="text-lg font-semibold">{strings.auth.configFailed}</h1>
        <ErrorMessage
          error={boot.error}
          onRetry={() => {
            setBoot({ status: 'loading' });
            setAttempt((n) => n + 1);
          }}
        />
      </div>
    );
  }
  return (
    <ApiContext.Provider value={boot.client}>
      <CurrentUser boot={boot}>{children}</CurrentUser>
    </ApiContext.Provider>
  );
}

function CurrentUser({ boot, children }: { boot: BootResult; children: ReactNode }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: queryKeys.me, queryFn: () => api.getMe(), staleTime: Infinity });
  const { userManager, config } = boot;

  useEffect(() => {
    if (!userManager) return;
    const reload = () => void queryClient.invalidateQueries({ queryKey: queryKeys.me });
    // The user removed (a sign-out here or in another tab, a failed renewal): a hide trigger (spec 6.10.18).
    const unloaded = () => endRevealSession();
    userManager.events.addUserLoaded(reload);
    userManager.events.addUserUnloaded(unloaded);
    return () => {
      userManager.events.removeUserLoaded(reload);
      userManager.events.removeUserUnloaded(unloaded);
    };
  }, [userManager, queryClient]);

  const state = useMemo<AuthState | null>(
    () =>
      me.data
        ? {
            config,
            me: me.data,
            signOut: userManager ? () => void signOut(userManager) : undefined,
          }
        : null,
    [config, me.data, userManager],
  );

  if (me.error) {
    return (
      <div className="p-4">
        <ErrorMessage error={me.error} onRetry={() => void me.refetch()} />
        {userManager && isApiError(me.error) && me.error.status === 401 && (
          <Button variant="primary" onClick={() => void userManager.signinRedirect()}>
            {strings.auth.signInAgain}
          </Button>
        )}
      </div>
    );
  }
  if (!state) return <Loading />;
  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}
